/**
 * ships-by-pill custom element — keeps the shipping-promise pill in sync with the
 * selected variant, and keeps the optional ship-by date honest under page caching.
 *
 * WHY IT EXISTS: Impact's <product-rerender allow-partial-rerender> only swaps a
 * hard-coded whitelist of block types on a variant change (price, buy-buttons,
 * liquid, ...), so a new block type goes stale the moment the shopper switches
 * variants. Rather than patch the theme's minified bundle, this element does for
 * itself exactly what the theme does for whitelisted blocks: listen for the form's
 * `product:rerender` event and replace itself with its counterpart from the
 * server-rendered fragment. The pre-order decision therefore stays server-side, in
 * Liquid, where the single predicate lives (PRD AC-15); this script decides nothing.
 *
 * Binding once to the form node at connect is safe by the theme's own design: the
 * <form is="product-form"> is rendered OUTSIDE the swapped <buy-buttons> element
 * (snippets/buy-buttons.liquid nests buy-buttons inside the form), and Impact's own
 * ProductRerender/ProductGallery bind to the form once at connect the same way — if
 * the form node were replaced on variant change, the theme's whole event chain would
 * already be broken. On `productChange` events the theme replaces the whole
 * product-info subtree itself (this element included), so we do nothing and let the
 * fresh element reconnect.
 *
 * DATE REFRESH: the Liquid-rendered <time class="ships-by-pill__date"> is computed at
 * render time, and Shopify CDN-caches storefront pages — a cached Friday render must
 * not keep promising Friday's date all weekend. On every connect (initial load AND
 * each self-replacement) the date is recomputed from the shopper's clock using the
 * calendar-day offset the server stamped on the element, matching the Liquid
 * fallback's semantics and format. Calendar days, not business days: the shopper
 * counts on their own calendar, and skipping weekends silently pushed the promise
 * two days past what the merchant wrote.
 */
class ShipsByPill extends HTMLElement {
  /** Six days is the longest a weekday name still names exactly one day. */
  static WEEKDAY_ONLY_MAX_OFFSET = 6;

  connectedCallback() {
    this._abortController = new AbortController();
    const form = document.forms[this.getAttribute("form")];
    form?.addEventListener("product:rerender", (event) => this._onRerender(event), {
      signal: this._abortController.signal,
    });
    this._refreshDate();
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  _onRerender(event) {
    // A bare CustomEvent from an app embed or future customization carries no detail;
    // never let it throw away the pill's only sync mechanism.
    if (!event.detail || !event.detail.htmlFragment || event.detail.productChange) {
      return;
    }
    const replacement = this._findInFragment(event.detail.htmlFragment);
    if (replacement) {
      this.replaceWith(replacement);
    }
  }

  _refreshDate() {
    const dateElement = this.querySelector("time.ships-by-pill__date");
    if (!dateElement) {
      return;
    }
    const raw = Number(dateElement.getAttribute("data-ships-by-offset"));
    if (!Number.isFinite(raw)) {
      return;
    }
    // Clamp to the schema's 1..21 range, mirroring the Liquid side — never bail on an
    // out-of-range value, because bailing leaves the CDN-stale server date on screen.
    const offset = Math.min(Math.max(Math.round(raw), 1), 21);
    // CALENDAR days, matching the Liquid fallback. setDate rolls month and year ends
    // over on its own, so no month-length arithmetic is needed here.
    const date = new Date();
    date.setDate(date.getDate() + offset);
    // The weekday alone while it still names one day, the full date once it does not —
    // the rule snippets/ships-by-pill.liquid states at length and computes the same way.
    // The two must agree: if they did not, the pill would change its own wording a moment
    // after the page painted, which reads as a glitch rather than a refresh.
    const format =
      offset <= ShipsByPill.WEEKDAY_ONLY_MAX_OFFSET
        ? { weekday: "long" }
        : { weekday: "short", month: "long", day: "numeric" };
    dateElement.textContent = new Intl.DateTimeFormat(
      document.documentElement.lang || "en",
      format,
    ).format(date);
  }

  /**
   * The fragment can nest content inside <template> elements (the theme's own
   * deepQuerySelector exists for the same reason), so recurse into template contents
   * when the top-level lookup misses.
   */
  _findInFragment(root) {
    const hit = root.querySelector(`ships-by-pill[id="${CSS.escape(this.id)}"]`);
    if (hit) {
      return hit;
    }
    for (const template of root.querySelectorAll("template")) {
      const nested = this._findInFragment(template.content);
      if (nested) {
        return nested;
      }
    }
    return null;
  }
}

if (!window.customElements.get("ships-by-pill")) {
  window.customElements.define("ships-by-pill", ShipsByPill);
}
